import React, { useMemo, useState } from "react";
import { toLocalDisplay } from "../../utils/datetimeLocal";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Search,
  X,
  Activity,
  Users,
  CheckCircle2,
  Ban,
  AlertTriangle,
  Clock,
  History,
  Loader2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Pencil,
  BookOpen,
  Trash2,
  Plus,
  Calendar,
  MapPin,
  ChevronsRight,
  Hash,
  Printer,
  Award,
  UserPlus,
  PenLine,
  Undo2,
  Unlock,
  Eraser,
} from "lucide-react";

import {
  useAdminGetDietQuery,
  useAdminGetExamAnalyticsQuery,
  useAdminGetDietApplicationsQuery,
  useAdminGradeDietApplicationMutation,
  useAdminGetCourseGradesQuery,
  useAdminSubmitCourseGradeMutation,
  useAdminDeleteCourseGradeMutation,
  useAdminCompileResultMutation,
  useAdminGetCentersQuery,
  useAdminChangeApplicationCenterMutation,
  useAdminDeferApplicationMutation,
  useAdminGetDietsByExamQuery,
  useAdminSetExamNoMutation,
  useAdminGetApplicationDeferralsQuery,
  useAdminGetExaminerMarksQuery,
  useAdminReleasePreviewQuery,
  useAdminPublishResultMutation,
  useAdminPublishResultsMutation,
  useAdminReleaseDietMutation,
  useAdminUnreleaseDietResultsMutation,
  useAdminUnreleaseResultMutation,
  useAdminUngradeApplicationMutation,
  useAdminUpdateDietResultSettingsMutation,
  useAdminManualCandidatesQuery,
  useAdminAddManualResultMutation,
  useAdminGetCoursesByExamQuery,
} from "../../api/examsApi";

import PageGate from "../../../../components/PageGate";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";
import sgSignature from "../../../../assets/sgsignature.jpeg";
import {
  letterSettings,
  substVars,
  toParagraphs,
  formatDietMonth,
  printResultLetter,
  isInterviewStatus,
  isInterview,
  isPositiveResult,
  resultLabel,
  DEFAULT_SUBJECT,
  DEFAULT_BODY_RECOMMENDED,
  DEFAULT_BODY_NOT_RECOMMENDED,
} from "../../utils/resultLetter";

/* ─── helpers ───────────────────────────────────────────────────────── */

const STATUS_COLORS: Record<string, string> = {
  draft:    "bg-gray-50 text-gray-400 border-gray-100",
  open:     "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed:   "bg-rose-50 text-rose-700 border-rose-100",
  archived: "bg-amber-50 text-amber-700 border-amber-100",
};

const APP_STATUS_COLORS: Record<string, string> = {
  draft:     "bg-gray-50 text-gray-500 border-gray-100",
  submitted: "bg-blue-50 text-blue-700 border-blue-100",
  paid:      "bg-cyan-50 text-cyan-700 border-cyan-100",
  approved:  "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected:  "bg-rose-50 text-rose-700 border-rose-100",
  completed: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

const BATCH_STATUS_COLORS: Record<string, string> = {
  // Course-average grading: an examiner has either marked everyone on the
  // course or is partway through.
  complete:          "bg-emerald-50 text-emerald-700 border-emerald-100",
  partial:           "bg-amber-50 text-amber-700 border-amber-100",
  submitted:         "bg-blue-50 text-blue-700 border-blue-100",
  under_review:      "bg-amber-50 text-amber-700 border-amber-100",
  rework:            "bg-orange-50 text-orange-700 border-orange-100",
  examiner_rework:   "bg-orange-50 text-orange-700 border-orange-100",
  moderation_rework: "bg-orange-50 text-orange-700 border-orange-100",
  approved:          "bg-emerald-50 text-emerald-700 border-emerald-100",
  under_moderation:  "bg-violet-50 text-violet-700 border-violet-100",
  moderated:         "bg-teal-50 text-teal-700 border-teal-100",
  published:         "bg-emerald-50 text-emerald-700 border-emerald-100",
};

/**
 * One candidate's result, exactly as that candidate sees it on their own
 * application page — same status wording, same figures, same letter.
 *
 * The admin needs to answer "what does this person see?" without logging in as
 * them, so this renders the member view rather than an admin paraphrase of it,
 * and prints through the same helper the member's own button calls.
 */
function CandidateResultModal({ app, diet, onClose }: { app: any; diet: any; onClose: () => void }) {
  const result = app?.result ?? null;
  const released = !!result?.published_at;
  // Real subjects only — an interview's single unit comes through with a null
  // course id and has nothing to tabulate.
  const subjects: any[] = (
    Array.isArray(result?.course_breakdown) ? result.course_breakdown : []
  ).filter((c: any) => c?.course_id != null);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-900 rounded-xl text-white"><Award className="w-4 h-4" /></div>
            <div>
              <div className="text-xs font-black text-gray-900 uppercase tracking-widest">Candidate Result</div>
              <div className="text-[10px] font-bold text-gray-400 mt-0.5">
                {app.user?.name} — {app.exam?.code} {app.exam_no ? `· ${app.exam_no}` : ""}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Whether the candidate can see any of this yet. */}
          <div
            className={`rounded-2xl px-4 py-3 text-[11px] font-bold border ${
              released
                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                : "bg-amber-50 border-amber-100 text-amber-700"
            }`}
          >
            {released
              ? `Released — the candidate has been able to see this since ${fmt(result.published_at)}.`
              : "Not released yet — the candidate still sees “Your result has not been published yet.”"}
          </div>

          {result ? (
            <div className="border border-gray-100 rounded-2xl p-5 space-y-2">
              <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-1">Result</div>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                  isPositiveResult(result.overall_status)
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                    : result.overall_status === "partial_pass"
                      ? "bg-amber-50 text-amber-700 border border-amber-100"
                      : result.overall_status === "absent"
                        ? "bg-gray-100 text-gray-600 border border-gray-200"
                        : "bg-rose-50 text-rose-700 border border-rose-100"
                }`}
              >
                {resultLabel(result.overall_status)}
              </span>
              {result.papers_sat != null && (
                <div className="text-xs text-gray-600">
                  {result.papers_passed}/{result.papers_sat} papers passed
                </div>
              )}
              {result.score != null && <div className="text-xs text-gray-600">Score: {result.score}</div>}
              {result.remarks && <p className="text-xs text-gray-500 italic">{result.remarks}</p>}
            </div>
          ) : (
            <div className="border border-gray-100 rounded-2xl p-8 text-center text-[11px] font-bold text-gray-400">
              This candidate has no compiled result yet.
            </div>
          )}

          {/* Subject marks. The printed statement shows only the code (P/F/ABS),
              so these figures are for the secretariat — they are what the codes
              above were derived from. */}
          {subjects.length > 0 && (
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-gray-50/70 border-b border-gray-100">
                <div className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                  Subject marks
                </div>
                <div className="text-[10px] font-bold text-gray-400 mt-0.5">
                  Not printed on the candidate's statement
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[9px] text-gray-400 uppercase tracking-widest border-b border-gray-100">
                    <th className="px-5 py-2 font-black">Subject</th>
                    <th className="px-5 py-2 font-black text-center">Score</th>
                    <th className="px-5 py-2 font-black text-center">Examiners</th>
                    <th className="px-5 py-2 font-black text-center">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {subjects.map((c: any) => (
                    <tr key={c.course_id}>
                      <td className="px-5 py-2.5">
                        <div className="font-semibold text-gray-800">{c.course_name ?? "—"}</div>
                        {c.course_code && (
                          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                            {c.course_code}
                          </div>
                        )}
                      </td>
                      {/* The average of the examiners' marks — what the
                          candidate is judged on, not any one examiner's mark. */}
                      <td className="px-5 py-2.5 text-center font-black tabular-nums text-gray-900">
                        {c.score ?? "—"}
                      </td>
                      <td className="px-5 py-2.5 text-center text-xs tabular-nums text-gray-500">
                        {c.markers ?? 0}
                      </td>
                      <td className="px-5 py-2.5 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                            c.passed === true
                              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                              : c.passed === false
                                ? "bg-rose-50 text-rose-700 border-rose-100"
                                : "bg-gray-50 text-gray-500 border-gray-100"
                          }`}
                          title={`Pass mark ${c.pass_mark}`}
                        >
                          {c.grade ?? "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={() => printResultLetter(app, diet)}
            disabled={!result}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-primary disabled:opacity-40 transition-all"
          >
            <Printer className="w-3.5 h-3.5" /> Print result letter
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── The marks the examiners actually recorded, by course and examiner ──── */
function BatchScores({ scores }: { scores: any[] }) {
  if (!scores.length) {
    return <div className="px-5 py-4 text-[11px] font-bold text-gray-400">No scores recorded.</div>;
  }

  return (
    <div className="overflow-x-auto border-t border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[9px] text-gray-400 uppercase tracking-widest bg-gray-50/60">
            <th className="px-5 py-2 font-black">Exam No.</th>
            <th className="px-5 py-2 font-black">Candidate</th>
            <th className="px-5 py-2 font-black">This mark</th>
            <th className="px-5 py-2 font-black">Course average</th>
            <th className="px-5 py-2 font-black">Outcome</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {scores.map((s: any) => (
            <tr key={s.id}>
              <td className="px-5 py-2 font-mono text-xs text-gray-600">{s.student_reg ?? "—"}</td>
              <td className="px-5 py-2 text-gray-800">{s.student_name ?? "—"}</td>
              {/* This examiner's own mark — on its own it decides nothing. */}
              <td className="px-5 py-2 font-bold text-gray-900">{s.score ?? "—"}</td>
              {/* What the candidate is actually judged on: the average of every
                  examiner who marked this course. */}
              <td className="px-5 py-2">
                <span className="font-black text-gray-900">{s.course_average ?? "—"}</span>
                {s.markers ? (
                  <span className="ml-1.5 text-[10px] font-bold text-gray-400">
                    from {s.markers} examiner{s.markers !== 1 ? "s" : ""}
                  </span>
                ) : null}
              </td>
              <td className="px-5 py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                    s.passed === null || s.passed === undefined
                      ? "bg-gray-50 text-gray-500 border-gray-100"
                      : s.passed
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : "bg-rose-50 text-rose-700 border-rose-100"
                  }`}
                  title={`Pass mark ${s.pass_mark}`}
                >
                  {s.passed === null || s.passed === undefined ? "—" : s.passed ? "Pass" : "Fail"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DietResultBatches({ dietId }: { dietId?: string }) {
  const { data: batches = [], isLoading, isFetching, refetch } = useAdminGetExaminerMarksQuery(
    { dietId: dietId! },
    { skip: !dietId }
  );
  const [openId, setOpenId] = React.useState<number | string | null>(null);

  return (
    <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
        <Activity className="w-4 h-4 text-indigo-600" />
        <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900 mr-auto">
          Examiner Marks ({batches.length})
        </h2>
        <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Read-only</span>
        <button onClick={() => refetch()} className="p-2 rounded-xl border-2 border-gray-100 hover:bg-gray-50 transition-all">
          <RefreshCw className={`w-3.5 h-3.5 text-indigo-500 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {isLoading ? (
        <div className="p-10 flex items-center justify-center text-gray-300">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : batches.length === 0 ? (
        <div className="p-10 text-center">
          <AlertCircle className="w-7 h-7 text-gray-200 mx-auto mb-2" />
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
            No examiner has recorded a mark in this diet yet
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {batches.map((b: any) => {
            // One row per course × examiner, so the key has to carry both.
            const open = openId === b.key;
            return (
              <div key={b.key}>
                <button
                  onClick={() => setOpenId(open ? null : b.key)}
                  className="w-full flex items-center gap-4 px-6 py-4 hover:bg-gray-50/60 transition-all text-left"
                >
                  <ChevronsRight className={`w-4 h-4 text-gray-300 transition-transform ${open ? "rotate-90" : ""}`} />
                  <div className="mr-auto">
                    <div className="text-sm font-black text-gray-900">
                      {b.course_name ?? "Course"}
                      {b.course_code ? (
                        <span className="ml-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          {b.course_code}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[11px] font-bold text-gray-400">
                      Examiner: {b.examiner_name ?? "—"} • {b.student_count ?? 0} of {b.expected_count ?? 0}{" "}
                      candidate{(b.expected_count ?? 0) !== 1 ? "s" : ""} marked
                      {b.course_average != null ? (
                        <>
                          {" • "}
                          <span className="text-gray-600">avg {b.course_average}</span>
                          {" • "}
                          <span className="text-gray-600">
                            {b.passed_count ?? 0} of {b.student_count ?? 0} passing
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${BATCH_STATUS_COLORS[b.status] ?? "bg-gray-50 text-gray-500 border-gray-100"}`}>
                    {String(b.status ?? "").replace(/_/g, " ")}
                  </span>
                </button>
                {open && <BatchScores scores={b.scores ?? []} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const RESULT_STATUS_COLORS: Record<string, string> = {
  pending:         "bg-gray-50 text-gray-400 border-gray-100",
  passed:          "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed:          "bg-rose-50 text-rose-700 border-rose-100",
  recommended:     "bg-teal-50 text-teal-700 border-teal-100",
  not_recommended: "bg-orange-50 text-orange-700 border-orange-100",
  partial_pass:    "bg-amber-50 text-amber-700 border-amber-100",
  referred:        "bg-amber-50 text-amber-700 border-amber-100",
  full_reset:      "bg-red-50 text-red-700 border-red-100",
  absent:          "bg-gray-100 text-gray-600 border-gray-200",
};

function fmt(dt?: string | null) {
  return toLocalDisplay(dt);
}

function safeMsg(err: any) {
  return err?.data?.message || err?.data?.error || err?.error || err?.message || "Something went wrong";
}

type SortDir = "asc" | "desc";
type SortCol = "name" | "status" | "result" | "submitted_at" | "center" | "exam_no";

function SortIcon({ col, active, dir }: { col: SortCol; active: SortCol; dir: SortDir }) {
  if (col !== active) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
  return dir === "asc"
    ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-500" />
    : <ArrowDown className="w-3 h-3 ml-1 text-indigo-500" />;
}

/* ─── Course-based grade modal ───────────────────────────────────────── */

function CourseGradeModal({
  app,
  onClose,
  onDone,
}: {
  app: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const courses: any[] = (app.exam?.courses ?? []).filter((c: any) => c.is_active);

  const { data: gradeData, isLoading, refetch } = useAdminGetCourseGradesQuery(app.id);
  const [submitGrade, { isLoading: submitting }] = useAdminSubmitCourseGradeMutation();
  const [deleteGrade, { isLoading: deleting }] = useAdminDeleteCourseGradeMutation();
  const [compileResult, { isLoading: compiling }] = useAdminCompileResultMutation();

  // per-course slot input state: { [courseId]: { slot, score, remarks } }
  const [inputs, setInputs] = useState<Record<number, { slot: string; score: string; remarks: string }>>({});
  const [compileStatus, setCompileStatus] = useState("passed");
  const [compileRemarks, setCompileRemarks] = useState("");
  const [showCompile, setShowCompile] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const courseGrades: any[] = gradeData ?? [];

  // Group fetched grades by course_id
  const gradesByCourse = useMemo(() => {
    const map: Record<number, any[]> = {};
    for (const cg of courseGrades) {
      if (!map[cg.course_id]) map[cg.course_id] = [];
      map[cg.course_id].push(cg);
    }
    return map;
  }, [courseGrades]);

  const onAddGrade = async (courseId: number) => {
    const inp = inputs[courseId];
    if (!inp?.score) { setToast("Enter a score first."); return; }
    if (!inp?.slot)  { setToast("Select a grader slot."); return; }
    try {
      await submitGrade({
        appId: app.id,
        body: {
          exam_course_id: courseId,
          score: Number(inp.score),
          remarks: inp.remarks || undefined,
        },
      }).unwrap();
      setInputs((p) => ({ ...p, [courseId]: { slot: "", score: "", remarks: "" } }));
      refetch();
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  const onDeleteGrade = async (gradeId: string) => {
    try {
      await deleteGrade({ gradeId, appId: app.id }).unwrap();
      refetch();
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  const onCompile = async () => {
    try {
      await compileResult({
        appId: app.id,
        body: { overall_status: compileStatus, remarks: compileRemarks || undefined },
      }).unwrap();
      onDone();
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  const canCompile = courses.every((c: any) => (gradesByCourse[c.id] ?? []).length >= 1);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[92vh]">

        {/* header */}
        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gray-900 rounded-2xl text-white">
              <BookOpen className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">
                Course Grading
              </h3>
              <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                {app.user?.name} — {app.exam?.code} — {courses.length} course{courses.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {toast && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-widest flex justify-between items-center">
              {toast}
              <button onClick={() => setToast(null)}><X className="w-4 h-4 opacity-50" /></button>
            </div>
          )}

          {isLoading && (
            <div className="py-10 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-300" />
            </div>
          )}

          {!isLoading && courses.map((course: any) => {
            const grades: any[] = gradesByCourse[course.id] ?? [];
            const avg = grades.length > 0
              ? (grades.reduce((s: number, g: any) => s + Number(g.score), 0) / grades.length).toFixed(2)
              : null;
            const inp = inputs[course.id] ?? { slot: "", score: "", remarks: "" };
            const takenSlots = grades.map((g: any) => String(g.grader_slot));
            const availableSlots = ["1", "2", "3"].filter((s) => !takenSlots.includes(s));

            return (
              <div key={course.id} className="border border-gray-100 rounded-2xl overflow-hidden">
                {/* course header */}
                <div className="px-5 py-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{course.code}</span>
                    <span className="text-sm font-bold text-gray-800">{course.name}</span>
                  </div>
                  {avg !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Avg:</span>
                      <span className="text-sm font-black text-gray-900">{avg}</span>
                      <span className="text-[9px] font-black text-gray-400">({grades.length}/3 grades)</span>
                    </div>
                  )}
                  {avg === null && (
                    <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">No grades yet</span>
                  )}
                </div>

                {/* existing grades */}
                {grades.length > 0 && (
                  <div className="divide-y divide-gray-50">
                    {grades.map((g: any) => (
                      <div key={g.id} className="px-5 py-3 flex items-center justify-between group">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-lg bg-gray-900 text-white text-[9px] font-black flex items-center justify-center">
                            {g.grader_slot}
                          </span>
                          <div>
                            <span className="text-sm font-black text-gray-900">{g.score}</span>
                            <span className="text-[9px] font-bold text-gray-400 ml-2">{g.grader?.name ?? "—"}</span>
                          </div>
                          {g.remarks && (
                            <span className="text-[10px] font-bold text-gray-400 italic truncate max-w-[120px]">{g.remarks}</span>
                          )}
                        </div>
                        <button
                          onClick={() => onDeleteGrade(g.id)}
                          disabled={deleting}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* add grade form */}
                {availableSlots.length > 0 && (
                  <div className="px-5 py-3 bg-indigo-50/30 border-t border-gray-50">
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={inp.slot}
                        onChange={(e) => setInputs((p) => ({ ...p, [course.id]: { ...inp, slot: e.target.value } }))}
                        className="border border-gray-200 rounded-xl px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-white outline-none w-28"
                      >
                        <option value="">Slot</option>
                        {availableSlots.map((s) => (
                          <option key={s} value={s}>Grader {s}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        placeholder="Score (0-100)"
                        value={inp.score}
                        onChange={(e) => setInputs((p) => ({ ...p, [course.id]: { ...inp, score: e.target.value } }))}
                        className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm font-bold text-gray-800 bg-white outline-none w-32 focus:border-indigo-400"
                      />
                      <input
                        type="text"
                        placeholder="Remarks (optional)"
                        value={inp.remarks}
                        onChange={(e) => setInputs((p) => ({ ...p, [course.id]: { ...inp, remarks: e.target.value } }))}
                        className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm font-bold text-gray-800 bg-white outline-none flex-1 focus:border-indigo-400"
                      />
                      <button
                        onClick={() => onAddGrade(course.id)}
                        disabled={submitting}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all disabled:opacity-30"
                      >
                        {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {availableSlots.length === 0 && (
                  <div className="px-5 py-2 bg-emerald-50/50 border-t border-gray-50">
                    <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">
                      All 3 grader slots filled
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* compile section */}
          {canCompile && (
            <div className="border-t border-gray-100 pt-4">
              <button
                onClick={() => setShowCompile((p) => !p)}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all"
              >
                <span>Compile Final Result</span>
                <CheckCircle2 className="w-4 h-4" />
              </button>

              {showCompile && (
                <div className="mt-4 space-y-4 bg-gray-50 rounded-2xl p-5">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Averages per course are computed from submitted grades. Set the overall verdict below.
                  </p>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Overall Status</label>
                    <div className="flex flex-wrap gap-2">
                      {["passed", "failed", "partial_pass", "recommended", "not_recommended"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setCompileStatus(s)}
                          className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                            compileStatus === s
                              ? "bg-gray-900 text-white border-gray-900"
                              : "bg-white text-gray-500 border-gray-200 hover:border-gray-500"
                          }`}
                        >
                          {s.replace(/_/g, " ")}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Chief Examiner Remarks</label>
                    <textarea
                      value={compileRemarks}
                      onChange={(e) => setCompileRemarks(e.target.value)}
                      placeholder="Optional overall remarks…"
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-400 bg-white resize-none"
                    />
                  </div>

                  <button
                    onClick={onCompile}
                    disabled={compiling}
                    className="w-full bg-indigo-600 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
                  >
                    {compiling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    {compiling ? "Compiling…" : "Confirm & Save Result"}
                  </button>
                </div>
              )}
            </div>
          )}

          {!canCompile && !isLoading && courses.length > 0 && (
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest text-center py-2">
              Add at least one grade per course to enable result compilation.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function PrintLetterModal({ app, diet, onClose }: { app: any; diet: any; onClose: () => void }) {
  const memberNo  = app.user?.member?.membership_no ?? "—";
  const name      = (app.user?.name ?? "").toUpperCase();
  const examCode  = (app.exam?.code ?? "").toUpperCase();
  const examLabel = examCode === "PCI"
    ? "Professional Competence Interview (PCI)"
    : "Mature Route Interview";
  const dietMonth = formatDietMonth(diet);
  const { signName, signTitle, refPrefix, issued, sigUrl, subjectTpl, bodyRecTpl, bodyNotRecTpl } = letterSettings(diet);
  const recommended = isPositiveResult(app.result?.overall_status);

  const vars: Record<string, string> = {
    name,
    exam: examLabel,
    exam_upper: examLabel.toUpperCase(),
    month: dietMonth,
    membership_no: memberNo,
  };
  const subject = substVars(subjectTpl, vars);
  const bodyParas = toParagraphs(substVars(recommended ? bodyRecTpl : bodyNotRecTpl, vars));

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-900 rounded-xl text-white"><Printer className="w-4 h-4" /></div>
            <div>
              <div className="text-xs font-black text-gray-900 uppercase tracking-widest">Result Letter Preview</div>
              <div className="text-[10px] font-bold text-gray-400 mt-0.5">{app.user?.name} — {examCode}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => printResultLetter(app, diet)}
              className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all"
            >
              <Printer className="w-3 h-3" /> Print
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* letter preview */}
        <div className="overflow-y-auto flex-1 p-8">
          <div className="border border-gray-200 rounded-xl p-10 font-serif text-[12px] leading-relaxed space-y-4" style={{ fontFamily: "'Times New Roman', serif" }}>
            {/* header */}
            <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-4">
              <div>
                <img src="/niqs-logo.png" alt="NIQS" className="h-20 object-contain" />
              </div>
              <div className="text-right text-[10px] leading-relaxed text-gray-700">
                No. 24, NIQS Crescent,<br/>
                Off Michael Ama Nnachi Crescent,<br/>
                Cadastral Zone B6, Mabushi District,<br/>
                P. O. Box 10689 Garki, Abuja<br/>
                Tel: 08028303346, 08167593807<br/>
                Email: info@niqs.org.ng<br/>
                www.niqs.org.ng
              </div>
            </div>

            {/* ref + date */}
            <div className="flex justify-between font-bold text-[11px]">
              <span>{refPrefix}{memberNo}</span>
              <span>DATE ISSUED: {issued}.</span>
            </div>

            {/* recipient */}
            <div className="mt-2">
              <div className="font-bold">{name}</div>
              <div className="font-bold mt-1">MEMBERSHIP NUMBER: {memberNo}</div>
              {app.user?.member?.address && (
                <div className="mt-1 text-gray-700 whitespace-pre-line">{app.user.member.address}</div>
              )}
            </div>

            {/* subject */}
            <div className="font-bold underline mt-4">
              {subject}
            </div>

            {/* body */}
            {bodyParas.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            {/* closing + signature */}
            <div className="mt-6">
              <p>Yours faithfully,</p>
              <p className="font-bold">For: The Nigerian Institute of Quantity Surveyors</p>
              <img
                src={sigUrl || sgSignature}
                alt="Signature"
                className="h-16 object-contain my-1"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              <div className="font-bold">{signName}</div>
              <div>{signTitle}</div>
            </div>

            <div className="text-[9px] italic text-gray-500 border-t border-gray-200 pt-3 mt-6">
              * Any alteration or erasure on this statement of result invalidates it.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Per-diet result-letter settings modal ──────────────────────────── */

function ResultSettingsModal({
  diet,
  onClose,
  onSaved,
}: {
  diet: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState<string>(diet?.result_signatory_name ?? "QS Ahmad Kabir Muhammad, FNIQS");
  const [title, setTitle] = useState<string>(diet?.result_signatory_title ?? "Secretary General");
  const [refPrefix, setRefPrefix] = useState<string>(diet?.result_ref_prefix ?? "NIQS/ECB/08C/A/");
  const [issuedDate, setIssuedDate] = useState<string>(
    diet?.result_issued_date ? String(diet.result_issued_date).slice(0, 10) : ""
  );
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [subject, setSubject] = useState<string>(diet?.result_subject ?? DEFAULT_SUBJECT);
  const [bodyRec, setBodyRec] = useState<string>(diet?.result_body_recommended ?? DEFAULT_BODY_RECOMMENDED);
  const [bodyNotRec, setBodyNotRec] = useState<string>(diet?.result_body_not_recommended ?? DEFAULT_BODY_NOT_RECOMMENDED);
  const [err, setErr] = useState<string | null>(null);

  const [save, { isLoading: saving }] = useAdminUpdateDietResultSettingsMutation();

  const onSubmit = async () => {
    setErr(null);
    try {
      const body = new FormData();
      body.append("result_signatory_name", name);
      body.append("result_signatory_title", title);
      body.append("result_ref_prefix", refPrefix);
      if (issuedDate) body.append("result_issued_date", issuedDate);
      body.append("result_subject", subject);
      body.append("result_body_recommended", bodyRec);
      body.append("result_body_not_recommended", bodyNotRec);
      if (signatureFile) body.append("signature", signatureFile);
      await save({ dietId: diet.id, body }).unwrap();
      onSaved();
    } catch (e: any) {
      setErr(e?.data?.message || "Could not save result settings.");
    }
  };

  const currentSig = signatureFile
    ? URL.createObjectURL(signatureFile)
    : diet?.result_signature_url || null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-xl text-white"><Pencil className="w-4 h-4" /></div>
            <div>
              <div className="text-xs font-black text-gray-900 uppercase tracking-widest">Result Letter Settings</div>
              <div className="text-[10px] font-bold text-gray-400 mt-0.5">{diet?.title}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500">✕</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Signatory name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Signatory title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reference prefix</label>
              <input value={refPrefix} onChange={(e) => setRefPrefix(e.target.value)}
                className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date issued</label>
              <input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)}
                className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
              <p className="text-[10px] text-gray-400 mt-1">Leave blank to use today's date.</p>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Subject / header line</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Body — recommended (pass)</label>
            <textarea value={bodyRec} onChange={(e) => setBodyRec(e.target.value)} rows={5}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Body — not recommended (fail)</label>
            <textarea value={bodyNotRec} onChange={(e) => setBodyNotRec(e.target.value)} rows={4}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
          </div>
          <p className="text-[10px] text-gray-400">
            Separate paragraphs with a blank line. Available placeholders:{" "}
            <span className="font-mono text-gray-600">{"{name}"}</span>,{" "}
            <span className="font-mono text-gray-600">{"{exam}"}</span>,{" "}
            <span className="font-mono text-gray-600">{"{exam_upper}"}</span>,{" "}
            <span className="font-mono text-gray-600">{"{month}"}</span>,{" "}
            <span className="font-mono text-gray-600">{"{membership_no}"}</span>.
          </p>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Signature image</label>
            <div className="mt-1 flex items-center gap-3">
              {currentSig && <img src={currentSig} alt="Signature" className="h-12 object-contain border rounded-lg p-1 bg-white" />}
              <input type="file" accept="image/png,image/jpeg" onChange={(e) => setSignatureFile(e.target.files?.[0] ?? null)}
                className="text-xs" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">PNG/JPG, max 2MB. Replaces the current signature on this diet's result letters.</p>
          </div>

          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={onSubmit} disabled={saving}
            className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Simple / Paper grade modal (unchanged) ─────────────────────────── */

function GradeModal({
  app,
  dietId,
  onClose,
  onDone,
}: {
  app: any;
  dietId: string;
  onClose: () => void;
  onDone: (updated: any) => void;
}) {
  const isPaperBased = (app.papers ?? []).length > 0;

  const [gradeApp, { isLoading: grading }] = useAdminGradeDietApplicationMutation();

  const [simpleStatus, setSimpleStatus] = useState<string>(
    app.result?.overall_status ?? "recommended"
  );
  const [simpleRemark, setSimpleRemark] = useState<string>(
    app.result?.remarks ?? ""
  );

  const [paperRemarks, setPaperRemarks] = useState<string>(
    app.result?.remarks ?? ""
  );

  const [paperGrades, setPaperGrades] = useState<Record<string, { status: string; score: string }>>(() => {
    const init: Record<string, { status: string; score: string }> = {};
    for (const p of app.papers ?? []) {
      const pid = p.exam_paper_id ?? p.paper?.id;
      if (!pid) continue;
      init[pid] = {
        status: p.status === "pending" ? "pass" : p.status,
        score: p.score != null ? String(p.score) : "",
      };
    }
    return init;
  });

  const [toast, setToast] = useState<string | null>(null);

  const onSubmit = async () => {
    try {
      let body: any;
      if (isPaperBased) {
        body = {
          papers: (app.papers ?? []).map((p: any) => {
            const pid = p.exam_paper_id ?? p.paper?.id;
            const g = paperGrades[pid] ?? { status: "pass", score: "" };
            return { exam_paper_id: pid, status: g.status, score: g.score !== "" ? Number(g.score) : null };
          }),
          remarks: paperRemarks.trim() || undefined,
        };
      } else {
        body = {
          overall_status: simpleStatus,
          remarks: simpleRemark.trim() || undefined,
        };
      }
      const result = await gradeApp({ dietId, appId: app.id, body }).unwrap();
      onDone(result);
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gray-900 rounded-2xl text-white">
              <Pencil className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Grade Application</h3>
              <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                {app.user?.name} — {app.exam?.code}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {toast && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-widest flex justify-between items-center">
              {toast}
              <button onClick={() => setToast(null)}><X className="w-4 h-4 opacity-50" /></button>
            </div>
          )}

          {isPaperBased ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-700">Paper Results</span>
              </div>
              {(app.papers ?? []).map((p: any) => {
                const pid = p.exam_paper_id ?? p.paper?.id;
                const g = paperGrades[pid] ?? { status: "pass", score: "" };
                return (
                  <div key={pid} className="bg-gray-50 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{p.paper?.code}</span>
                        <span className="ml-2 text-xs font-bold text-gray-700">{p.paper?.title}</span>
                      </div>
                      {p.paper?.pass_mark && (
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Pass mark: {p.paper.pass_mark}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {["pass", "fail", "absent"].map((s) => (
                          <button
                            key={s}
                            onClick={() => setPaperGrades((prev) => ({ ...prev, [pid]: { ...g, status: s } }))}
                            className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                              g.status === s
                                ? s === "pass" ? "bg-emerald-600 text-white" : s === "fail" ? "bg-rose-600 text-white" : "bg-gray-600 text-white"
                                : "bg-white border border-gray-200 text-gray-500 hover:border-gray-400"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Score</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={g.score}
                          onChange={(e) => setPaperGrades((prev) => ({ ...prev, [pid]: { ...g, score: e.target.value } }))}
                          placeholder="—"
                          className="w-16 border-b-2 border-gray-200 py-1 text-center text-sm font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Examiner Remarks <span className="normal-case font-medium text-gray-300">(optional, max 50 words)</span>
                </label>
                <textarea
                  rows={3}
                  value={paperRemarks}
                  onChange={(e) => setPaperRemarks(e.target.value)}
                  placeholder="Overall examiner notes for this candidate…"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none focus:border-indigo-400 bg-white resize-none"
                />
                <div className="text-right text-[9px] font-bold text-gray-300">
                  {paperRemarks.trim().split(/\s+/).filter(Boolean).length} / 50 words
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Outcome</label>
                <div className="grid grid-cols-3 gap-3">
                  {["recommended", "partial_pass", "full_reset", "not_recommended", "absent"].map((s) => {
                    const selectedColor =
                      s === "recommended"     ? "bg-teal-600 text-white border-teal-600"
                      : s === "partial_pass"  ? "bg-amber-600 text-white border-amber-600"
                      : s === "full_reset"    ? "bg-red-600 text-white border-red-600"
                      : s === "not_recommended" ? "bg-orange-600 text-white border-orange-600"
                      : "bg-gray-600 text-white border-gray-600";
                    return (
                      <button
                        key={s}
                        onClick={() => setSimpleStatus(s)}
                        className={`py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${
                          simpleStatus === s
                            ? selectedColor
                            : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        {resultLabel(s)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Examiner Remarks <span className="normal-case font-medium text-gray-300">(optional, max 50 words)</span>
                </label>
                <textarea
                  rows={3}
                  value={simpleRemark}
                  onChange={(e) => setSimpleRemark(e.target.value)}
                  placeholder="Overall examiner notes for this candidate…"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none focus:border-indigo-400 bg-white resize-none"
                />
                <div className="text-right text-[9px] font-bold text-gray-300">
                  {simpleRemark.trim().split(/\s+/).filter(Boolean).length} / 50 words
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={grading}
            className="flex-[2] bg-gray-900 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
          >
            {grading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Submit Grade"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A result for someone who never registered.
 *
 * Old sittings are still being brought onto the portal, and the people who sat
 * them never applied through it — so there is no application to grade. This
 * creates both at once: the registration and the result it carries. The
 * registration is stamped as entered by hand, and the register says so, because
 * it is not the same thing as a candidate who applied and was marked.
 *
 * Closed diets only, super admin only — while a diet is still open, the answer
 * to "this candidate is not registered" is that they register.
 */
function ManualResultModal({
  diet,
  dietId,
  onClose,
  onDone,
}: {
  diet: any;
  dietId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<any | null>(null);

  // The server refuses under two characters and caps the answer at fifteen
  // rows, so the only thing left to spare is the request itself.
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data: candidates = [], isFetching: searching } = useAdminManualCandidatesQuery(
    { dietId, q: debounced },
    { skip: debounced.length < 2 || !!picked }
  );

  // An interview is recommended or not; a course exam is passed, referred or
  // failed. Offering both vocabularies at once would invite a TPC result of
  // "Full Reset", which means nothing on a course exam.
  // The exam's own flag decides, not its code: an exam flagged as an interview
  // under some other code has no subjects either, and asking for them would put
  // course lines on a sitting that was one decision.
  const interview = isInterview(diet?.exam);
  const OUTCOMES = interview
    ? ["recommended", "partial_pass", "full_reset", "not_recommended", "absent"]
    : ["passed", "referred", "failed", "absent"];

  const [status, setStatus] = useState(interview ? "recommended" : "passed");
  const [score, setScore] = useState("");
  const [examNo, setExamNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("");

  /* The subjects. An interview is one decision with nothing to tabulate; every
     other exam is a subject list, and the candidate's statement of result is
     built from the subjects they registered for and the marks against them —
     so a result entered without them hands the candidate a verdict on a blank
     sheet. Withdrawn courses are left out: they are no longer part of the
     examination and must not print as though the candidate sat them. */
  const { data: allCourses = [], isFetching: coursesLoading } = useAdminGetCoursesByExamQuery(
    diet?.exam?.id ?? "",
    { skip: interview || !diet?.exam?.id }
  );
  const courses = useMemo(
    () => (allCourses as any[]).filter((c) => c.is_active),
    [allCourses]
  );

  const [passMark, setPassMark] = useState("50");
  // subject id → the mark, where "" is a subject registered but never marked
  // (which is what ABS looks like on a statement). Absent from the map means
  // the candidate did not register for it at all.
  const [subjects, setSubjects] = useState<Record<string, string>>({});

  // Every active subject, registered by default — the ordinary case is a
  // candidate who sat the whole exam. Unticking is for a referral carried into
  // this diet, where only the outstanding subjects count.
  React.useEffect(() => {
    if (interview || courses.length === 0) return;
    setSubjects((prev) =>
      Object.keys(prev).length > 0
        ? prev
        : Object.fromEntries(courses.map((c: any) => [String(c.id), ""]))
    );
  }, [courses, interview]);

  const toggleSubject = (id: string) =>
    setSubjects((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = "";
      return next;
    });

  // What the marks come to, shown as they are typed. The stated outcome still
  // stands — this is a decision the board already took and a certificate the
  // Institute may already have issued — but a total that contradicts it is
  // worth seeing before saving.
  const marks = Object.values(subjects).filter((m) => m !== "").map(Number);
  const subjectCount = Object.keys(subjects).length;
  const markedCount = marks.length;
  const passedCount = marks.filter((m) => m >= Number(passMark || 0)).length;
  const average = markedCount > 0
    ? Math.round(marks.reduce((sum, m) => sum + m, 0) / markedCount)
    : 0;
  // Only meaningful once the sitting itself has gone out — a single released
  // result still waits on the diet's release before a candidate can see it.
  const [release, setRelease] = useState(!!diet?.results_released_at);

  const [addManualResult, { isLoading: saving }] = useAdminAddManualResultMutation();
  const [toast, setToast] = useState<string | null>(null);

  const submit = async () => {
    if (!picked) return setToast("Choose the candidate first.");
    if (reason.trim().length < 5)
      return setToast("Say why this result is being entered by hand.");

    try {
      const res: any = await addManualResult({
        dietId,
        body: {
          user_id: picked.user_id,
          overall_status: status,
          score: score !== "" ? Number(score) : null,
          exam_no: examNo.trim() || null,
          remarks: remarks.trim() || null,
          reason: reason.trim(),
          release_to_candidate: release,
          // A subject with no mark still travels — it is registered and prints
          // as ABS, which is a different thing from never having taken it.
          ...(interview
            ? {}
            : {
                pass_mark: passMark !== "" ? Number(passMark) : 50,
                courses: Object.entries(subjects).map(([id, mark]) => ({
                  exam_course_id: Number(id),
                  score: mark !== "" ? Number(mark) : null,
                })),
              }),
        },
      }).unwrap();

      // This API answers some errors with HTTP 200 and a bare {message}, which
      // RTK hands back as success. Only a payload naming the new registration
      // is a real answer.
      if (!res?.application_id) {
        setToast(res?.message || "Could not enter this result.");
        return;
      }

      onDone(res?.message ?? "Result entered.");
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gray-900 rounded-2xl text-white">
              <UserPlus className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Enter a result by hand</h3>
              <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                {diet?.title} — creates the registration too
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {toast && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-widest flex justify-between items-center gap-3">
              <span className="normal-case tracking-normal font-bold">{toast}</span>
              <button onClick={() => setToast(null)}><X className="w-4 h-4 opacity-50 shrink-0" /></button>
            </div>
          )}

          <div className="p-3 rounded-2xl bg-amber-50 border border-amber-100 flex gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] font-bold text-amber-800 leading-relaxed">
              This registers the candidate in a closed diet and records their result in one act —
              no application, no payment, no approval stands behind it. The registration is marked
              as entered by hand, with your name and reason on it.
            </p>
          </div>

          {/* Candidate */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Candidate</label>

            {picked ? (
              <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-2xl px-4 py-3">
                <div>
                  <div className="font-black text-gray-900 text-sm">{picked.name}</div>
                  <div className="text-[10px] font-bold text-gray-500 mt-0.5">
                    {picked.membership_no ?? "No membership number"}
                    {picked.email ? ` • ${picked.email}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => { setPicked(null); setQuery(""); setDebounced(""); }}
                  className="text-[9px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800 shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Name, membership number or email…"
                    className="w-full pl-9 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-bold outline-none focus:border-indigo-400"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-300" />
                  )}
                </div>

                {debounced.length >= 2 && (
                  <div className="border border-gray-100 rounded-2xl divide-y divide-gray-50 max-h-56 overflow-y-auto">
                    {candidates.length === 0 && !searching && (
                      <p className="px-4 py-4 text-[10px] font-black uppercase tracking-widest text-gray-300">
                        No member found — anyone already in this diet is left out
                      </p>
                    )}
                    {candidates.map((m: any) => (
                      <button
                        key={m.user_id}
                        onClick={() => setPicked(m)}
                        className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors"
                      >
                        <div className="font-black text-gray-900 text-xs">{m.name}</div>
                        <div className="text-[10px] font-bold text-gray-400 mt-0.5">
                          {m.membership_no ?? "—"}
                          {m.email ? ` • ${m.email}` : ""}
                          {m.grade ? ` • ${m.grade}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Outcome */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Outcome</label>
            <div className="grid grid-cols-3 gap-3">
              {OUTCOMES.map((s) => {
                const selectedColor =
                  s === "recommended" || s === "passed" ? "bg-teal-600 text-white border-teal-600"
                  : s === "partial_pass" || s === "referred" ? "bg-amber-600 text-white border-amber-600"
                  : s === "full_reset" || s === "failed"     ? "bg-red-600 text-white border-red-600"
                  : s === "not_recommended"                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-gray-600 text-white border-gray-600";
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${
                      status === s ? selectedColor : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {resultLabel(s)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Exam No <span className="normal-case font-medium text-gray-300">(optional)</span>
              </label>
              <input
                value={examNo}
                onChange={(e) => setExamNo(e.target.value)}
                placeholder="—"
                className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-indigo-400"
              />
            </div>
            {interview ? (
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Panel Score <span className="normal-case font-medium text-gray-300">(optional, 0–100)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder="—"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-indigo-400"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Pass Mark <span className="normal-case font-medium text-gray-300">(per subject)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={passMark}
                  onChange={(e) => setPassMark(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-indigo-400"
                />
              </div>
            )}
          </div>

          {/* Subjects — course exams only; an interview is one unit with
              nothing to tabulate. */}
          {!interview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-700">
                  Subjects ({subjectCount})
                </span>
                {subjectCount > 0 && (
                  <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-gray-400">
                    {markedCount} marked
                    {markedCount > 0 && ` • avg ${average} • ${passedCount}/${subjectCount} passed`}
                  </span>
                )}
              </div>

              <p className="text-[10px] font-bold text-gray-400 leading-relaxed">
                These are what the candidate's statement of result prints. Untick a subject they
                did not sit; leave a mark blank and that subject reads ABS.
              </p>

              {coursesLoading && (
                <div className="py-6 text-center">
                  <Loader2 className="w-5 h-5 mx-auto animate-spin text-gray-200" />
                </div>
              )}

              {!coursesLoading && courses.length === 0 && (
                <p className="px-4 py-4 rounded-2xl bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-300">
                  This exam has no active subjects — only the outcome above will be recorded
                </p>
              )}

              <div className="space-y-2">
                {courses.map((c: any) => {
                  const key = String(c.id);
                  const registered = key in subjects;
                  const mark = subjects[key] ?? "";
                  const passed = mark !== "" ? Number(mark) >= Number(passMark || 0) : null;

                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-3 rounded-2xl px-4 py-3 border transition-all ${
                        registered ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={registered}
                        onChange={() => toggleSubject(key)}
                        className="w-4 h-4 accent-indigo-600 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{c.code}</div>
                        <div className="text-xs font-bold text-gray-700 truncate">{c.name}</div>
                      </div>
                      {registered && passed !== null && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border shrink-0 ${
                            passed
                              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                              : "bg-rose-50 text-rose-700 border-rose-100"
                          }`}
                        >
                          {passed ? "Pass" : status === "referred" ? "RF" : "Fail"}
                        </span>
                      )}
                      <input
                        type="number"
                        min={0}
                        max={100}
                        disabled={!registered}
                        value={mark}
                        onChange={(e) =>
                          setSubjects((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        placeholder="—"
                        className="w-16 border-b-2 border-gray-200 py-1 text-center text-sm font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent disabled:opacity-40 shrink-0"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Remarks <span className="normal-case font-medium text-gray-300">(optional — the candidate sees these)</span>
            </label>
            <textarea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Notes that belong on the result itself…"
              className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none focus:border-indigo-400 resize-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Why by hand? <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Sat the 2019 diet before the portal — result taken from the board's minutes."
              className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none focus:border-indigo-400 resize-none"
            />
            <p className="text-[10px] font-bold text-gray-400">
              Kept on the registration, beside your name — this is the only account of where the result came from.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={release}
              onChange={(e) => setRelease(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-indigo-600"
            />
            <span className="text-[11px] font-bold text-gray-600 leading-relaxed">
              Release this result to the candidate
              {!diet?.results_released_at && (
                <span className="block text-[10px] font-bold text-amber-600 mt-0.5">
                  This diet has not been released yet — the candidate sees it once it is.
                </span>
              )}
            </span>
          </label>
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !picked}
            className="flex-[2] bg-gray-900 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Register & record result"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── main page ──────────────────────────────────────────────────────── */

// Marking is the examiners' and the Chief Examiner's work. Either the role or
// the capability opens it — a role check alone would miss a secretariat account
// deliberately granted exam.grade.manage, and a permission check alone would
// shut out an examiner before the seeder has been re-run.
const GRADE_ROLES = ["examiner", "committee_examination_board.chairman"];
// Only the Chief Examiner (Examination Board Chairman) may publish results.
// Releasing a sitting to the membership is the secretariat's act — the
// Examination Board publishes (closes grading); the admin decides when
// candidates get to see it.
//
// Gated on the capability rather than on a role name. `canRelease` was hardcoded
// `true`, which was the right instinct at the time — the route was admin-only
// and re-testing a role here would only have hidden the control from the people
// it belongs to. Now that exam.result.release exists as its own permission there
// is something precise to ask, and asking it means an admin who does not hold it
// no longer sees a button that the API will refuse.

export default function AdminExamDietDetailsPage() {
  const { can } = useAccess();
  const canGrade = can.anyRole(GRADE_ROLES) || can.perm(PERMS.exam.gradeManage);
  const canRelease = can.perm(PERMS.exam.resultRelease);

  const { id: examId = "", dietId = "" } = useParams();

  const { data: diet, isLoading: dietLoading, refetch: refetchDiet } =
    useAdminGetDietQuery(dietId, { skip: !dietId });

  const { data: analytics, isLoading: analyticsLoading, refetch: refetchAnalytics } =
    useAdminGetExamAnalyticsQuery({ examId, dietId }, { skip: !examId || !dietId });

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: "submitted_at", dir: "desc" });
  const [gradeTarget, setGradeTarget] = useState<any | null>(null);
  const [centerTarget, setCenterTarget] = useState<any | null>(null);
  const [selectedCenterId, setSelectedCenterId] = useState('');

  const { data: centers = [] } = useAdminGetCentersQuery();
  const [changeCenter, { isLoading: changingCenter }] = useAdminChangeApplicationCenterMutation();

  const [deferTarget, setDeferTarget] = useState<any | null>(null);
  const [selectedDietId, setSelectedDietId] = useState('');
  const { data: examDiets = [] } = useAdminGetDietsByExamQuery(examId, { skip: !examId });
  const [deferApp, { isLoading: deferring }] = useAdminDeferApplicationMutation();

  const [examNoTarget, setExamNoTarget] = useState<any | null>(null);
  const [examNoInput, setExamNoInput] = useState('');
  const [setExamNo, { isLoading: savingExamNo }] = useAdminSetExamNoMutation();

  const [printTarget, setPrintTarget] = useState<any | null>(null);
  const [resultTarget, setResultTarget] = useState<any | null>(null);
  const [showResultSettings, setShowResultSettings] = useState(false);

  // Backfilling a sitting — see ManualResultModal. Offered only to the super
  // admin, and only on a diet nobody can still apply to: a diet left `open`
  // with a closing date months gone is closed in every sense that matters,
  // which is the same test the server applies.
  const [showManualResult, setShowManualResult] = useState(false);
  const canManualResult = can.role("super_admin");

  // Deleting a result is the Board's call, not the secretariat's — the same two
  // roles the API's ensureChief() accepts, so the button is not offered to
  // someone it will refuse.
  const canUngrade = can.anyRole(["super_admin", "committee_examination_board.chairman"]);
  const dietClosed =
    diet?.status === "closed" ||
    diet?.status === "archived" ||
    (!!diet?.applications_close_at && new Date(diet.applications_close_at) < new Date());

  const [historyTarget, setHistoryTarget] = useState<any | null>(null);
  const { data: deferrals = [], isLoading: deferralsLoading } =
    useAdminGetApplicationDeferralsQuery(historyTarget?.id ?? '', { skip: !historyTarget });

  const {
    data: rawApplications = [],
    isLoading: appsLoading,
    isFetching: appsFetching,
    refetch: refetchApps,
  } = useAdminGetDietApplicationsQuery(
    { dietId, ...(statusFilter ? { status: statusFilter } : {}) },
    { skip: !dietId }
  );

  // Where the diet stands between the board's publication and our release, so
  // the button says what it will do instead of failing on click.
  //
  // This API answers errors with HTTP 200 and a `{message}` body, so RTK hands
  // them back as success. Only a payload carrying real counts is a real answer —
  // anything else (403, a missing column, an error body) must fall through to
  // the plain button rather than render "Awaiting board · of".
  const { data: releasePayload } = useAdminReleasePreviewQuery({ dietId }, { skip: !dietId });
  const releaseState =
    typeof (releasePayload as any)?.candidates === "number" ? releasePayload : undefined;

  // The gate that decides what candidates can actually READ. A result carries
  // its own release stamp, but the sitting has to have gone out as well before
  // the candidate's own screen loads it at all — so a result released on its
  // own in a diet still awaiting the board is stamped and invisible, and saying
  // "Released" on that row is how this page used to mislead us.
  const dietReleased = !!(diet?.results_released_at || releaseState?.results_released_at);

  // Who the board still owes a result. Named on the amber chip, because the
  // count on its own ("5 of 69") left the five to be hunted down by hand.
  const [showOutstanding, setShowOutstanding] = useState(false);
  const outstanding = releaseState?.outstanding ?? [];

  const [publishResult, { isLoading: publishingOne }] = useAdminPublishResultMutation();
  const [publishResults, { isLoading: publishingAll }] = useAdminPublishResultsMutation();
  const [releaseDiet, { isLoading: openingDiet }] = useAdminReleaseDietMutation();
  const [unreleaseAll, { isLoading: withdrawingAll }] = useAdminUnreleaseDietResultsMutation();
  const [unreleaseOne, { isLoading: withdrawingOne }] = useAdminUnreleaseResultMutation();
  const [ungradeApp, { isLoading: ungrading }] = useAdminUngradeApplicationMutation();

  // Graded in error. The result is deleted outright, so it asks first, and asks
  // separately about the examiners' marks — a verdict compiled off good marks
  // only needs recompiling, and throwing three examiners' work away to fix it
  // would be its own mistake.
  const [ungradeTarget, setUngradeTarget] = useState<any | null>(null);

  const [localApps, setLocalApps] = useState<any[]>([]);
  const applications = localApps.length > 0 ? localApps : (rawApplications as any[]);

  const doReleaseOne = async (appId: string) => {
    try {
      const res: any = await publishResult({ dietId: dietId!, appId }).unwrap();
      setLocalApps([]);
      refetchApps();
      refetchDiet();
      // The server says whether the candidate can actually see it: releasing one
      // result in a diet that has not gone out stamps the record and shows the
      // candidate nothing. Swallowing that message is what turned a half-open
      // gate into "I released it and he still has no result".
      if (res?.awaiting_diet_release) {
        window.alert(
          res.message ||
            "Result released, but this diet has not been released to candidates yet — " +
              "it becomes visible when the diet is released.",
        );
      }
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not release this result.");
    }
  };

  const doReleaseAll = async () => {
    // Releasing is what the membership sees, so it is spelled out rather than
    // hidden behind the word "publish" the board already used.
    if (
      !window.confirm(
        "Release this diet's results to candidates?\n\n" +
          "Every result the Chief Examiner has published becomes visible to its candidate." +
          (dietReleased
            ? "\n\nThis diet is already open, so they are visible the moment this goes through."
            : " The whole diet must be published by the board first."),
      )
    )
      return;
    try {
      const res: any = await publishResults({ dietId: dietId! }).unwrap();
      setLocalApps([]);
      refetchApps();
      refetchDiet();
      window.alert(res?.message || "Results released.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not release results.");
    }
  };

  // Open the sitting's own gate without releasing anybody. This is the way out
  // of a diet the board has not finished: results released one at a time become
  // visible at once instead of waiting on the last candidate to be marked.
  const doOpenDiet = async () => {
    if (
      !window.confirm(
        "Open this diet to candidates?\n\n" +
          "Candidates whose results you have released will see them immediately, and anyone " +
          "you release from here on sees theirs at once.\n\n" +
          "Nobody else is released by this: a candidate with no released result still sees nothing.",
      )
    )
      return;
    try {
      const res: any = await releaseDiet({ dietId: dietId! }).unwrap();
      setLocalApps([]);
      refetchApps();
      refetchDiet();
      window.alert(res?.message || "Diet opened.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not open this diet.");
    }
  };

  const doWithdrawAll = async () => {
    if (
      !window.confirm(
        "Withdraw this diet's results from candidates?\n\n" +
          "Every released result goes back out of sight and the diet closes again. " +
          "Candidates who have already read or printed theirs will have seen it.\n\n" +
          "The board's publication stands — grading stays closed until the Chief Examiner reopens it.",
      )
    )
      return;
    try {
      const res: any = await unreleaseAll({ dietId: dietId! }).unwrap();
      setLocalApps([]);
      refetchApps();
      refetchDiet();
      window.alert(res?.message || "Results withdrawn.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not withdraw these results.");
    }
  };

  const doWithdrawOne = async (app: any) => {
    if (
      !window.confirm(
        `Withdraw ${app.user?.name ?? "this candidate"}'s result?\n\n` +
          "They can no longer see it. The rest of the sitting is untouched.",
      )
    )
      return;
    try {
      const res: any = await unreleaseOne({ dietId: dietId!, appId: app.id }).unwrap();
      setLocalApps([]);
      refetchApps();
      refetchDiet();
      window.alert(res?.message || "Result withdrawn.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not withdraw this result.");
    }
  };

  // clearMarks drops the examiners' marks as well. See ungradeTarget.
  const doUngrade = async (clearMarks: boolean) => {
    if (!ungradeTarget) return;
    try {
      const res: any = await ungradeApp({
        appId: ungradeTarget.id,
        dietId: dietId!,
        clear_marks: clearMarks,
      }).unwrap();
      setUngradeTarget(null);
      setLocalApps([]);
      refetchApps();
      refetchAnalytics();
      window.alert(res?.message || "Result deleted.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not ungrade this candidate.");
    }
  };

  React.useEffect(() => {
    if ((rawApplications as any[]).length > 0) setLocalApps(rawApplications as any[]);
  }, [rawApplications]);

  const searched = useMemo(() => {
    if (!search.trim()) return applications;
    const t = search.toLowerCase();
    return applications.filter((a: any) =>
      (a.user?.name ?? "").toLowerCase().includes(t) ||
      (a.user?.email ?? "").toLowerCase().includes(t) ||
      (a.exam?.code ?? "").toLowerCase().includes(t)
    );
  }, [applications, search]);

  const filtered = useMemo(() => {
    const list = [...searched];
    list.sort((a, b) => {
      let av: any, bv: any;
      switch (sort.col) {
        case "name":         av = a.user?.name ?? "";              bv = b.user?.name ?? "";              break;
        case "status":       av = a.status ?? "";                  bv = b.status ?? "";                  break;
        case "result":       av = a.result?.overall_status ?? "";  bv = b.result?.overall_status ?? "";  break;
        case "center":       av = a.center?.name ?? "";            bv = b.center?.name ?? "";            break;
        case "submitted_at": av = a.submitted_at ?? "";            bv = b.submitted_at ?? "";            break;
        case "exam_no":      av = a.exam_no ?? "";                 bv = b.exam_no ?? "";                 break;
        default: av = ""; bv = "";
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [searched, sort]);

  const toggleSort = (col: SortCol) =>
    setSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
    );

  async function submitExamNo() {
    if (!examNoTarget) return;
    try {
      await setExamNo({ applicationId: examNoTarget.id, exam_no: examNoInput.trim() || null }).unwrap();
      setExamNoTarget(null);
      setExamNoInput('');
      refetchApps();
    } catch (err) {
      alert(safeMsg(err));
    }
  }

  async function submitDefer() {
    if (!deferTarget || !selectedDietId) return;
    try {
      await deferApp({ applicationId: deferTarget.id, target_diet_id: selectedDietId }).unwrap();
      setDeferTarget(null);
      setSelectedDietId('');
      refetchApps();
    } catch (err) {
      alert(safeMsg(err));
    }
  }

  async function submitCenterChange() {
    if (!centerTarget || !selectedCenterId) return;
    try {
      await changeCenter({ applicationId: centerTarget.id, exam_center_id: selectedCenterId }).unwrap();
      setCenterTarget(null);
      setSelectedCenterId('');
      refetchApps();
    } catch (err) {
      alert(safeMsg(err));
    }
  }

  const handleGradeDone = (updated: any) => {
    if (updated) setLocalApps((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setGradeTarget(null);
    refetchApps();
  };

  const totals = analytics?.totals ?? {};
  const byStatus = analytics?.by_status ?? {};

  const statCards = [
    { label: "Total",     val: totals.applications ?? 0, icon: Users,        color: "gray"    },
    { label: "Submitted", val: byStatus.submitted   ?? 0, icon: Clock,        color: "blue"    },
    { label: "Paid",      val: byStatus.paid        ?? 0, icon: CheckCircle2, color: "cyan"    },
    { label: "Approved",  val: byStatus.approved    ?? 0, icon: CheckCircle2, color: "emerald" },
    { label: "Rejected",  val: byStatus.rejected    ?? 0, icon: Ban,          color: "rose"    },
    { label: "Completed", val: byStatus.completed   ?? 0, icon: Activity,     color: "indigo"  },
  ];

  const isBusy = dietLoading || analyticsLoading || appsLoading || appsFetching;

  const Th = ({ col, label }: { col: SortCol; label: string }) => (
    <th
      className="px-6 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer select-none hover:text-gray-700 transition-colors"
      onClick={() => toggleSort(col)}
    >
      <span className="flex items-center gap-0.5">
        {label}
        <SortIcon col={col} active={sort.col} dir={sort.dir} />
      </span>
    </th>
  );

  return (
    <PageGate perm={PERMS.exam.access}>
      <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-4">
            <Link
              to={`/exams/admin/exams/${examId}`}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors text-[10px] font-black uppercase tracking-widest"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Exam
            </Link>
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-[2rem] bg-gray-900 flex items-center justify-center text-white shadow-2xl">
                <History className="w-8 h-8" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none">
                    {dietLoading ? "Loading…" : (diet?.title ?? "Diet Details")}
                  </h1>
                  {diet?.status && (
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${STATUS_COLORS[diet.status] ?? ""}`}>
                      {diet.status}
                    </span>
                  )}
                </div>
                <p className="text-gray-400 text-xs mt-2 font-bold uppercase tracking-widest">
                  Open: {fmt(diet?.applications_open_at)}
                  <span className="mx-2">•</span>
                  Close: {fmt(diet?.applications_close_at)}
                  <span className="mx-2">•</span>
                  {filtered.length} Application{filtered.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start md:self-auto">
            {canRelease &&
              (dietReleased ? (
                <>
                  <span
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black uppercase tracking-widest"
                    title={`Released ${fmt(diet?.results_released_at ?? releaseState?.results_released_at)}`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Released to candidates
                  </span>
                  {/* An open diet still holds back results nobody has released
                      — the ones the board closed after it went out, or the rest
                      of a sitting opened early. One press hands those over. */}
                  {(releaseState?.to_release ?? 0) > 0 && (
                    <button
                      onClick={doReleaseAll}
                      disabled={publishingAll}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                      title="Release the results in this diet that have not been handed to their candidates yet"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {publishingAll ? "Releasing…" : `Release ${releaseState?.to_release} more`}
                    </button>
                  )}
                  {/* Released in error. Takes the whole sitting back and closes
                      the diet, which is also what unblocks the Chief Examiner's
                      reopen — that call refuses while anything is released. */}
                  <button
                    onClick={doWithdrawAll}
                    disabled={withdrawingAll}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-rose-100 text-rose-600 hover:bg-rose-50 hover:border-rose-200 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                    title="Take every released result back out of candidates' hands and close this diet again"
                  >
                    <Undo2 className="w-4 h-4" />
                    {withdrawingAll ? "Withdrawing…" : "Withdraw release"}
                  </button>
                </>
              ) : releaseState && !releaseState.ready ? (
                // The board has not finished publishing. Say how much is left
                // rather than offering an act that will be refused — and open
                // the names on a press, because nothing on this page moves
                // until those candidates are closed by the Chief Examiner.
                <>
                  <button
                    onClick={() => setShowOutstanding(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all text-[10px] font-black uppercase tracking-widest"
                    title="The Chief Examiner must publish the whole diet before the sitting can go out in full — press to see who is outstanding"
                  >
                    <Clock className="w-4 h-4" />
                    Awaiting board · {releaseState.awaiting_board} of {releaseState.candidates}
                  </button>
                  {/* The way through an unfinished sitting: open the diet's own
                      gate so the results released one at a time actually reach
                      their candidates. Releases nobody by itself. */}
                  <button
                    onClick={doOpenDiet}
                    disabled={openingDiet}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-indigo-600 text-white hover:bg-indigo-700 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                    title="Open this diet so results you release one at a time become visible — releases nobody by itself"
                  >
                    <Unlock className="w-4 h-4" />
                    {openingDiet ? "Opening…" : "Open diet for single releases"}
                  </button>
                </>
              ) : (
                <button
                  onClick={doReleaseAll}
                  disabled={publishingAll}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                  title={
                    releaseState?.closed_by_grading
                      ? `Release this diet's results to candidates. ${releaseState.closed_by_grading} of them were marked on the older grading screen rather than published by the board — releasing closes those off.`
                      : "Release this diet's results to candidates"
                  }
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {publishingAll
                    ? "Releasing…"
                    : `Release results to candidates${
                        releaseState?.to_release ? ` (${releaseState.to_release})` : ""
                      }`}
                </button>
              ))}
            {canManualResult && dietClosed && (
              <button
                onClick={() => setShowManualResult(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-gray-100 hover:bg-amber-50 hover:border-amber-200 transition-all text-[10px] font-black uppercase tracking-widest text-slate-700"
                title="Register a candidate who never applied and record their result — closed diets only"
              >
                <UserPlus className="w-4 h-4 text-amber-500" />
                Add result by hand
              </button>
            )}
            <button
              onClick={() => setShowResultSettings(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-gray-100 hover:bg-indigo-50 hover:border-indigo-200 transition-all text-[10px] font-black uppercase tracking-widest text-slate-700"
              title="Edit result-letter signatory, signature, date and reference prefix"
            >
              <Pencil className="w-4 h-4 text-indigo-500" />
              Result settings
            </button>
            <Link
              to="/exams/admin/sessions"
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-gray-100 hover:bg-orange-50 hover:border-orange-200 transition-all text-[10px] font-black uppercase tracking-widest text-slate-700"
            >
              <Calendar className="w-4 h-4 text-orange-500" />
              Timetable
            </Link>
            <Link
              to="/exams/admin/sessions"
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-gray-100 hover:bg-slate-900 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest text-slate-700"
            >
              <BookOpen className="w-4 h-4" />
              All Cards
            </Link>
            <button
              onClick={() => { refetchDiet(); refetchAnalytics(); refetchApps(); setLocalApps([]); }}
              className="p-3.5 rounded-2xl border-2 border-gray-100 hover:bg-gray-50 transition-all"
            >
              <RefreshCw className={`w-4 h-4 text-indigo-500 ${isBusy ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-[2rem] p-5 shadow-sm flex flex-col gap-2">
              <div className={`w-9 h-9 rounded-xl bg-${s.color}-50 flex items-center justify-center shrink-0`}>
                <s.icon className={`w-4 h-4 text-${s.color}-500`} />
              </div>
              <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{s.label}</div>
              <div className="text-2xl font-black text-gray-900 tracking-tighter">
                {analyticsLoading ? <Loader2 className="w-4 h-4 animate-spin text-gray-300" /> : s.val}
              </div>
            </div>
          ))}
        </div>

        {/* Examiner-submitted result batches (read-only — same data the Chief Examiner reviews) */}
        <DietResultBatches dietId={dietId} />

        {/* Applications Table */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex items-center gap-3 mr-auto">
              <Users className="w-4 h-4 text-indigo-600" />
              <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
                Applications ({filtered.length})
              </h2>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Name, email or exam code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-4 ring-indigo-50 transition-all outline-none"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
                </button>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-40 py-2 px-3 bg-white border border-gray-200 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="paid">Paid</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-50 text-left">
                  <Th col="name" label="Applicant" />
                  <Th col="exam_no" label="Exam No" />
                  <Th col="center" label="Center" />
                  <Th col="status" label="Status" />
                  <Th col="result" label="Result" />
                  <Th col="submitted_at" label="Submitted" />
                  <th className="px-6 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {appsLoading && (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-200" />
                    </td>
                  </tr>
                )}
                {!appsLoading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <Users className="w-10 h-10 mx-auto text-gray-100 mb-3" />
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No applications found</p>
                    </td>
                  </tr>
                )}
                {filtered.map((app: any) => {
                  const result = app.result;
                  const papers: any[] = app.papers ?? [];
                  const courses: any[] = app.exam?.courses ?? [];
                  const hasCourses = courses.length > 0;
                  const isGraded = !!result;
                  const isPublished = !!result?.published_at;

                  return (
                    <tr
                      key={app.id}
                      className={`group transition-all ${
                        isPublished ? "bg-emerald-50/60 hover:bg-emerald-50" : "hover:bg-gray-50/80"
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-black text-gray-900 text-sm">{app.user?.name ?? "—"}</div>
                        <div className="text-[10px] font-bold text-gray-400 mt-0.5">{app.user?.email}</div>
                        {hasCourses && (
                          <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-0.5">
                            {courses.length} course{courses.length !== 1 ? "s" : ""}
                          </div>
                        )}
                        {/* No application stands behind this registration — the
                            secretariat typed it in. Said on the row, not only in
                            the audit columns, so the register never reads as
                            though this candidate applied and was marked. */}
                        {app.is_manual_entry && (
                          <div
                            className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full text-[8px] font-black uppercase tracking-widest border bg-amber-50 text-amber-700 border-amber-200"
                            title={
                              "Registration and result entered by hand" +
                              (app.manual_entry_by_name ? ` by ${app.manual_entry_by_name}` : "") +
                              (app.manual_entry_at ? ` on ${fmt(app.manual_entry_at)}` : "") +
                              (app.manual_entry_reason ? ` — ${app.manual_entry_reason}` : "")
                            }
                          >
                            <PenLine className="w-2.5 h-2.5 shrink-0" /> Added manually
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {app.exam_no ? (
                          <span className="font-black text-[11px] text-gray-900 uppercase tracking-wider">{app.exam_no}</span>
                        ) : (
                          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs font-bold text-gray-600 uppercase tracking-tight">{app.center?.name ?? "—"}</div>
                        {app.center?.state && (
                          <div className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase">{app.center.state}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${APP_STATUS_COLORS[app.status] ?? ""}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {result ? (() => {
                          const isInterview = isInterviewStatus(result.overall_status);
                          return (
                            <div className="space-y-1.5">
                              {isInterview ? (
                                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-[9px] font-black uppercase tracking-widest border ${RESULT_STATUS_COLORS[result.overall_status] ?? ""}`}>
                                  {isPositiveResult(result.overall_status) ? (
                                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                                  ) : (
                                    <Ban className="w-3 h-3 shrink-0" />
                                  )}
                                  {resultLabel(result.overall_status)}
                                </div>
                              ) : (
                                <span className={`inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${RESULT_STATUS_COLORS[result.overall_status] ?? ""}`}>
                                  {result.overall_status?.replace(/_/g, " ")}
                                </span>
                              )}
                              {result.is_override && (
                                <div
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border bg-amber-50 text-amber-700 border-amber-200"
                                  title={
                                    `Emergency override by the Board Chairman` +
                                    (result.override_by_name ? ` (${result.override_by_name})` : "") +
                                    (result.override_at ? ` on ${fmt(result.override_at)}` : "") +
                                    (result.override_reason ? ` — ${result.override_reason}` : "")
                                  }
                                >
                                  <AlertTriangle className="w-2.5 h-2.5 shrink-0" /> Overridden by Chairman
                                </div>
                              )}
                              {result.remarks && (
                                <div className="max-w-[220px]">
                                  <p className="text-[10px] font-medium text-gray-500 italic leading-relaxed line-clamp-2" title={result.remarks}>
                                    {result.remarks}
                                  </p>
                                </div>
                              )}
                              {!isInterview && result.papers_sat != null && (
                                <div className="text-[9px] font-bold text-gray-400 tracking-tighter">
                                  {result.papers_passed}/{result.papers_sat} papers passed
                                </div>
                              )}
                              {!isInterview && result.score != null && (
                                <div className="text-[9px] font-bold text-gray-400 tracking-tighter">
                                  Avg: {result.score}
                                </div>
                              )}
                            </div>
                          );
                        })() : (
                          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Not graded</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                          {app.submitted_at ? fmt(app.submitted_at) : "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!["MRI","PCI"].includes((app.exam?.code ?? "").toUpperCase()) && (
                            <Link
                              to={`/exams/admin/applications/${app.id}/courses`}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                            >
                              <BookOpen className="w-3 h-3" />
                              Courses
                            </Link>
                          )}
                          {!["MRI","PCI"].includes((app.exam?.code ?? "").toUpperCase()) && (
                            <Link
                              to={`/exams/admin/applications/${app.id}/card`}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 transition-all"
                            >
                              <BookOpen className="w-3 h-3" />
                              Card
                            </Link>
                          )}
                          {/* Exam number stays editable even after grading */}
                          <button
                            onClick={() => { setExamNoTarget(app); setExamNoInput(app.exam_no ?? ''); }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-teal-600 hover:border-teal-200 hover:bg-teal-50 transition-all"
                          >
                            <Hash className="w-3 h-3" />
                            Exam No
                          </button>

                          {/* Defer & reassign are locked once the application is graded */}
                          {!isGraded && (
                            <>
                              <button
                                onClick={() => { setDeferTarget(app); setSelectedDietId(''); }}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-violet-600 hover:border-violet-200 hover:bg-violet-50 transition-all"
                              >
                                <ChevronsRight className="w-3 h-3" />
                                Defer
                              </button>
                              <button
                                onClick={() => { setCenterTarget(app); setSelectedCenterId(app.center?.id ?? ''); }}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 transition-all"
                              >
                                <MapPin className="w-3 h-3" />
                                Reassign
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setHistoryTarget(app)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
                          >
                            <History className="w-3 h-3" />
                            History
                          </button>
                          {canGrade && !isGraded && (
                            <button
                              onClick={() => setGradeTarget(app)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                            >
                              <Pencil className="w-3 h-3" />
                              Grade
                            </button>
                          )}
                          {/* Every graded candidate, not just interviews — the
                              admin has to be able to see and print what any one
                              of them will see. */}
                          {result && (
                            <button
                              onClick={() => setResultTarget(app)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                              title="View this candidate's result as they see it, and print their letter"
                            >
                              <Award className="w-3 h-3" />
                              Result
                            </button>
                          )}
                          {isInterview(app.exam) && result && (
                            <button
                              onClick={() => setPrintTarget(app)}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all ${
                                isPositiveResult(result?.overall_status)
                                  ? "border-teal-200 text-teal-600 hover:bg-teal-50"
                                  : "border-orange-200 text-orange-600 hover:bg-orange-50"
                              }`}
                            >
                              <Printer className="w-3 h-3" />
                              Print
                            </button>
                          )}
                          {/* Two gates, in order: the board publishes (closes
                              grading), then the secretariat releases (the
                              candidate sees it). */}
                          {result && (
                            result.published_at && !dietReleased ? (
                              // Stamped, and the candidate still sees nothing:
                              // both gates have to be open (see dietReleased).
                              <span
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200"
                                title={`Released ${fmt(result.published_at)}, but this diet has not been released to candidates — the candidate cannot see it yet`}
                              >
                                <Clock className="w-3 h-3" /> Held · diet closed
                              </span>
                            ) : result.published_at ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-100"
                                title={`Released to the candidate ${fmt(result.published_at)}`}
                              >
                                <CheckCircle2 className="w-3 h-3" /> Released
                              </span>
                            ) : !result.finalized_at ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-100"
                                title="The Chief Examiner has not published this result yet"
                              >
                                <Clock className="w-3 h-3" /> Awaiting board
                              </span>
                            ) : canRelease ? (
                              <button
                                onClick={() => doReleaseOne(app.id)}
                                disabled={publishingOne}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 text-[9px] font-black uppercase tracking-widest text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
                                title={
                                  dietReleased
                                    ? "Release this one result to its candidate"
                                    : "Release this one result. This diet is still closed, so the candidate sees it only once the diet is opened."
                                }
                              >
                                <CheckCircle2 className="w-3 h-3" /> Release
                              </button>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100"
                                title="Published by the board — awaiting release by the secretariat"
                              >
                                <Clock className="w-3 h-3" /> Awaiting release
                              </span>
                            )
                          )}
                          {/* Released in error — this candidate only. */}
                          {result?.published_at && canRelease && (
                            <button
                              onClick={() => doWithdrawOne(app)}
                              disabled={withdrawingOne}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-rose-200 text-[9px] font-black uppercase tracking-widest text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50"
                              title="Take this result back — the candidate can no longer see it"
                            >
                              <Undo2 className="w-3 h-3" /> Withdraw
                            </button>
                          )}
                          {/* Graded in error. Hidden while the result is
                              released: the candidate has read it, so the
                              release is what gets reversed first. */}
                          {result && !result.published_at && canUngrade && (
                            <button
                              onClick={() => setUngradeTarget(app)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-orange-200 text-[9px] font-black uppercase tracking-widest text-orange-600 hover:bg-orange-50 transition-all"
                              title="Delete this result and send the candidate back to ungraded"
                            >
                              <Eraser className="w-3 h-3" /> Ungrade
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!appsLoading && filtered.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-50 bg-gray-50/30 flex items-center justify-between">
              <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                Showing {filtered.length} of {applications.length} application{applications.length !== 1 ? "s" : ""}
              </span>
              {search && (
                <button onClick={() => setSearch("")} className="text-[9px] font-black text-indigo-500 hover:text-indigo-700 uppercase tracking-widest">
                  Clear filter
                </button>
              )}
            </div>
          )}
        </div>

        {/* Paper stats breakdown */}
        {(analytics?.papers ?? []).length > 0 && (
          <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="p-8 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-indigo-600" />
              <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">Paper Results Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-50 text-left">
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">Paper</th>
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Total</th>
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Pass</th>
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Fail</th>
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Absent</th>
                    <th className="px-8 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Pass Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(analytics.papers as any[]).map((p: any) => (
                    <tr key={p.paper_id} className="hover:bg-gray-50/80 transition-all">
                      <td className="px-8 py-4">
                        <div className="font-black text-[10px] text-indigo-600 uppercase tracking-widest">{p.code}</div>
                        <div className="text-xs font-bold text-gray-700 mt-0.5">{p.title}</div>
                      </td>
                      <td className="px-8 py-4 text-center font-black text-gray-900">{p.total}</td>
                      <td className="px-8 py-4 text-center font-black text-emerald-600">{p.pass}</td>
                      <td className="px-8 py-4 text-center font-black text-rose-500">{p.fail}</td>
                      <td className="px-8 py-4 text-center font-black text-gray-400">{p.absent}</td>
                      <td className="px-8 py-4 text-center">
                        {p.pass_rate !== null ? (
                          <span className={`font-black text-sm ${p.pass_rate >= 50 ? "text-emerald-600" : "text-rose-500"}`}>
                            {p.pass_rate}%
                          </span>
                        ) : <span className="text-gray-300 font-bold text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Set Exam No Modal */}
      {examNoTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setExamNoTarget(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-teal-600 rounded-2xl text-white">
                  <Hash className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Set Exam Number</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {examNoTarget.user?.name} — {examNoTarget.exam?.code}
                  </p>
                </div>
              </div>
              <button onClick={() => setExamNoTarget(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Exam Number</label>
                <input
                  type="text"
                  value={examNoInput}
                  onChange={(e) => setExamNoInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitExamNo()}
                  placeholder="e.g. TPC/2026/01/001"
                  className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-black text-gray-900 uppercase outline-none focus:border-teal-400 bg-white tracking-wider"
                  autoFocus
                />
              </div>
              <p className="text-[10px] text-gray-400 font-medium leading-relaxed">
                Leave blank to clear the exam number. Must be unique across all applications.
              </p>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setExamNoTarget(null)}
                className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={savingExamNo}
                onClick={submitExamNo}
                className="flex-[2] bg-teal-600 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-teal-700 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {savingExamNo ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defer Application Modal */}
      {deferTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setDeferTarget(null)}
          />
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-violet-600 rounded-2xl text-white">
                  <ChevronsRight className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Defer Application</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {deferTarget.user?.name} — {deferTarget.exam?.code}
                  </p>
                </div>
              </div>
              <button onClick={() => setDeferTarget(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
                <div className="text-[9px] font-black text-violet-500 uppercase tracking-widest mb-1">Current Diet</div>
                <div className="font-black text-gray-900 text-sm uppercase">
                  {deferTarget.diet?.title ?? '—'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Target Diet</label>
                <select
                  value={selectedDietId}
                  onChange={(e) => setSelectedDietId(e.target.value)}
                  className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-violet-400 bg-white"
                >
                  <option value="">— Select a diet —</option>
                  {examDiets
                    .filter((d: any) => d.id !== deferTarget.exam_diet_id)
                    .map((d: any) => (
                      <option key={d.id} value={d.id}>{d.title}</option>
                    ))}
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 text-[10px] font-bold text-amber-700 leading-relaxed">
                If the applicant's current center is not available in the target diet, it will be cleared and must be reassigned.
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setDeferTarget(null)}
                className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={deferring || !selectedDietId}
                onClick={submitDefer}
                className="flex-[2] bg-violet-600 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-violet-700 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {deferring ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Deferring…</> : 'Confirm Deferral'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Center Modal */}
      {centerTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setCenterTarget(null)}
          />
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-500 rounded-2xl text-white">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Reassign Center</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {centerTarget.user?.name} — {centerTarget.exam?.code}
                  </p>
                </div>
              </div>
              <button onClick={() => setCenterTarget(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                <div className="text-[9px] font-black text-amber-500 uppercase tracking-widest mb-1">Current Center</div>
                <div className="font-black text-gray-900 text-sm uppercase">
                  {centerTarget.center?.name ?? '—'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">New Center</label>
                <select
                  value={selectedCenterId}
                  onChange={(e) => setSelectedCenterId(e.target.value)}
                  className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-amber-400 bg-white"
                >
                  <option value="">— Select a center —</option>
                  {centers.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.code ? `${c.code} — ` : ''}{c.name}
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-[10px] text-gray-400 font-medium leading-relaxed">
                The admission card number will be reassigned automatically on the next card view.
              </p>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setCenterTarget(null)}
                className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={changingCenter || !selectedCenterId || selectedCenterId === centerTarget.center?.id}
                onClick={submitCenterChange}
                className="flex-[2] bg-amber-500 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-amber-600 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {changingCenter ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : 'Confirm Reassignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deferral History Modal */}
      {historyTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={() => setHistoryTarget(null)}
          />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-slate-800 rounded-2xl text-white">
                  <History className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Deferral History</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {historyTarget.user?.name} — {historyTarget.exam?.code}
                  </p>
                </div>
              </div>
              <button onClick={() => setHistoryTarget(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {deferralsLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              )}
              {!deferralsLoading && deferrals.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-slate-400 font-black uppercase tracking-widest text-xs">No deferral records found.</p>
                </div>
              )}
              {!deferralsLoading && deferrals.length > 0 && (
                <div className="space-y-3">
                  {deferrals.map((d: any) => (
                    <div key={d.id} className="border border-slate-100 rounded-2xl p-4 bg-slate-50/50">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                          {d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                        {d.deferred_by && (
                          <span className="text-[9px] font-bold text-slate-400">· by {d.deferred_by.name}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                        <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">From Diet</div>
                          <div className="text-xs font-black text-slate-800">{d.from_diet?.title ?? '—'}</div>
                          {d.from_center && (
                            <div className="text-[9px] text-slate-500 font-bold mt-1">{d.from_center.name}</div>
                          )}
                        </div>
                        <ChevronsRight className="w-4 h-4 text-violet-400 shrink-0" />
                        <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 text-center">
                          <div className="text-[9px] font-black text-violet-400 uppercase tracking-widest mb-1">To Diet</div>
                          <div className="text-xs font-black text-slate-800">{d.to_diet?.title ?? '—'}</div>
                          {d.to_center && (
                            <div className="text-[9px] text-slate-500 font-bold mt-1">{d.to_center.name}</div>
                          )}
                        </div>
                      </div>
                      {d.note && (
                        <p className="mt-3 text-[10px] text-slate-500 italic leading-relaxed">{d.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* One candidate's result, as that candidate sees it */}
      {resultTarget && (
        <CandidateResultModal
          app={resultTarget}
          diet={diet}
          onClose={() => setResultTarget(null)}
        />
      )}

      {/* Print Result Letter */}
      {printTarget && (
        <PrintLetterModal
          app={printTarget}
          diet={diet}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {showManualResult && diet && (
        <ManualResultModal
          diet={diet}
          dietId={dietId}
          onClose={() => setShowManualResult(false)}
          onDone={(message) => {
            setShowManualResult(false);
            setLocalApps([]);
            refetchApps();
            refetchAnalytics();
            window.alert(message);
          }}
        />
      )}

      {/* Who the board still owes a result. The release is held by these rows
          and by nothing else, and until it goes out no candidate in the diet
          can read anything — including the ones already released by hand. */}
      {showOutstanding && releaseState && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowOutstanding(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-amber-50/50 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-500 rounded-2xl text-white">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Awaiting the board</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {releaseState.awaiting_board} of {releaseState.candidates} candidate
                    {releaseState.candidates !== 1 ? "s" : ""} not closed
                  </p>
                </div>
              </div>
              <button onClick={() => setShowOutstanding(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4 space-y-2">
                <p className="text-[11px] font-bold text-amber-800 leading-relaxed">
                  The Chief Examiner has to publish the whole sitting before the secretariat can
                  release it. Publishing records the candidates below as <b>absent</b> where no
                  examiner entered a mark, which is what closes them.
                </p>
                <p className="text-[11px] font-bold text-amber-800 leading-relaxed">
                  Until the diet is opened, <b>no candidate in it can see a result</b> — a result
                  released on its own row is stamped and still invisible.
                  {typeof releaseState.held_by_diet === "number" && releaseState.held_by_diet > 0 && (
                    <>
                      {" "}
                      {releaseState.held_by_diet} result{releaseState.held_by_diet !== 1 ? "s are" : " is"} sitting
                      in that state now.
                    </>
                  )}
                </p>
                <p className="text-[11px] font-bold text-amber-800 leading-relaxed">
                  If you do not want to wait for them, <b>Open diet for single releases</b> opens the
                  gate without releasing anybody: the results you have released become visible, and
                  the candidates below stay unseen until they are marked and released themselves.
                </p>
              </div>

              {outstanding.length === 0 ? (
                <p className="text-center text-slate-400 font-black uppercase tracking-widest text-[10px] py-6">
                  The server did not name them — refresh this page.
                </p>
              ) : (
                <div className="space-y-2">
                  {outstanding.map((o) => (
                    <div
                      key={o.application_id}
                      className="flex items-center justify-between gap-3 border border-slate-100 rounded-2xl px-4 py-3 bg-slate-50/50"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-black text-slate-800 truncate">{o.name ?? "—"}</div>
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                          {o.exam_no ?? "No exam no"}
                        </div>
                      </div>
                      <span className="shrink-0 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        {o.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-50 bg-gray-50/40 shrink-0">
              <Link
                to={`/exams/chief/diets/${dietId}/grading`}
                onClick={() => setShowOutstanding(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-2xl bg-gray-900 text-white hover:bg-black transition-all text-[10px] font-black uppercase tracking-[0.2em]"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Open the board's grading screen
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Graded in error. Two outcomes, because they are different mistakes:
          a wrong verdict off good marks only needs the result deleted and
          recompiled, while marks entered against the wrong candidate have to
          go as well. */}
      {ungradeTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setUngradeTarget(null)} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-orange-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-orange-500 rounded-2xl text-white">
                  <Eraser className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Ungrade candidate</h3>
                  <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                    {ungradeTarget.user?.name ?? "—"}
                    {ungradeTarget.exam_no ? ` · ${ungradeTarget.exam_no}` : ""}
                  </p>
                </div>
              </div>
              <button onClick={() => setUngradeTarget(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                <p className="text-[11px] font-bold text-orange-800 leading-relaxed">
                  The result (
                  <b>{ungradeTarget.result?.overall_status ?? "—"}</b>
                  ) is <b>deleted</b> and the application goes back to <b>approved</b>, as though the
                  candidate had never been marked. Grading reopens on them whether or not the board
                  had published the diet.
                </p>
              </div>

              <button
                onClick={() => doUngrade(false)}
                disabled={ungrading}
                className="w-full text-left rounded-2xl border-2 border-gray-100 hover:border-orange-200 hover:bg-orange-50/40 transition-all p-4 disabled:opacity-50"
              >
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-900">
                  Delete the result · keep the marks
                </div>
                <div className="text-[11px] font-bold text-gray-400 mt-1 leading-relaxed">
                  The examiners' marks stay, so the Chief Examiner can recompile. Use this for a
                  wrong verdict, a wrong pass mark, or a result compiled too early.
                </div>
              </button>

              <button
                onClick={() => doUngrade(true)}
                disabled={ungrading}
                className="w-full text-left rounded-2xl border-2 border-rose-100 hover:border-rose-300 hover:bg-rose-50/60 transition-all p-4 disabled:opacity-50"
              >
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-700">
                  Delete the result and the marks
                </div>
                <div className="text-[11px] font-bold text-rose-400 mt-1 leading-relaxed">
                  Every examiner's mark for this candidate is deleted too and the courses must be
                  marked again from scratch. Use this only when the marks belong to someone else.
                </div>
              </button>

              <button
                onClick={() => setUngradeTarget(null)}
                className="w-full rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 transition-colors"
              >
                {ungrading ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResultSettings && diet && (
        <ResultSettingsModal
          diet={diet}
          onClose={() => setShowResultSettings(false)}
          onSaved={() => { setShowResultSettings(false); refetchDiet(); }}
        />
      )}

      {/* Grade Modal — routes to correct modal based on exam type */}
      {gradeTarget && (() => {
        const hasCourses = (gradeTarget.exam?.courses ?? []).length > 0;
        if (hasCourses) {
          return (
            <CourseGradeModal
              app={gradeTarget}
              onClose={() => setGradeTarget(null)}
              onDone={() => handleGradeDone(null)}
            />
          );
        }
        return (
          <GradeModal
            app={gradeTarget}
            dietId={dietId}
            onClose={() => setGradeTarget(null)}
            onDone={handleGradeDone}
          />
        );
      })()}
    </PageGate>
  );
}
